import React, { useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Button,
  Tooltip,
} from '@mui/material';
import PaletteIcon from '@mui/icons-material/Palette';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  COLOR_SCALE_PRESETS,
  DEFAULT_PRESET_NAME,
  hexToRgb01,
  rgb01ToHex,
  stopsToCssGradient,
} from '../config/colorScaleConfig';

/**
 * Floating panel to edit the wind speed color scale in realtime.
 * Controlled component: value = { presetName, stops }, onChange(newValue).
 */
function ColorScaleEditor({ value, onChange, windSpeedRange = [0, 10] }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { presetName, stops } = value;
  const [minSpeed, maxSpeed] = windSpeedRange;

  const toSpeed = (pos) => (minSpeed + pos * (maxSpeed - minSpeed)).toFixed(1);

  const update = (newStops, newPresetName = 'Custom') => {
    onChange({ presetName: newPresetName, stops: newStops });
  };

  const handlePresetChange = (event) => {
    const name = event.target.value;
    if (COLOR_SCALE_PRESETS[name]) {
      update(COLOR_SCALE_PRESETS[name].map((s) => ({ ...s })), name);
    }
  };

  const handleColorChange = (index, color) => {
    update(stops.map((s, i) => (i === index ? { ...s, color } : s)));
  };

  const handlePosChange = (index, pos) => {
    update(stops.map((s, i) => (i === index ? { ...s, pos } : s)));
  };

  const handleDelete = (index) => {
    if (stops.length <= 2) return;
    update(stops.filter((_, i) => i !== index));
  };

  const handleAddStop = () => {
    const sorted = [...stops].sort((a, b) => a.pos - b.pos);
    let bestGap = -1;
    let left = sorted[0];
    let right = sorted[sorted.length - 1];
    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = sorted[i + 1].pos - sorted[i].pos;
      if (gap > bestGap) {
        bestGap = gap;
        left = sorted[i];
        right = sorted[i + 1];
      }
    }
    const leftRgb = hexToRgb01(left.color);
    const rightRgb = hexToRgb01(right.color);
    const blended = rgb01ToHex([
      (leftRgb[0] + rightRgb[0]) / 2,
      (leftRgb[1] + rightRgb[1]) / 2,
      (leftRgb[2] + rightRgb[2]) / 2,
    ]);
    update([...stops, { pos: (left.pos + right.pos) / 2, color: blended }]);
  };

  const handleCopy = async () => {
    const text = JSON.stringify(
      [...stops]
        .sort((a, b) => a.pos - b.pos)
        .map((s) => ({ pos: Math.round(s.pos * 100) / 100, color: s.color })),
      null,
      2
    );
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      // Clipboard API unavailable (e.g. non-HTTPS): show copyable prompt
      window.prompt('Copy the color config:', text);
    }
  };

  const handleReset = () => {
    update(COLOR_SCALE_PRESETS[DEFAULT_PRESET_NAME].map((s) => ({ ...s })), DEFAULT_PRESET_NAME);
  };

  if (!open) {
    return (
      <Tooltip title="Color scale">
        <IconButton
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 5,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            '&:hover': { backgroundColor: 'white' },
          }}
        >
          <PaletteIcon />
        </IconButton>
      </Tooltip>
    );
  }

  const knownPreset = COLOR_SCALE_PRESETS[presetName] ? presetName : 'Custom';
  const displayStops = stops
    .map((stop, index) => ({ ...stop, index }))
    .sort((a, b) => a.pos - b.pos);

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        right: 16,
        bottom: 16,
        zIndex: 5,
        width: 320,
        maxHeight: '70vh',
        overflow: 'auto',
        p: 2,
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight="bold">
            Color scale
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <FormControl size="small" fullWidth>
          <InputLabel id="color-preset-label">Preset</InputLabel>
          <Select
            labelId="color-preset-label"
            label="Preset"
            value={knownPreset}
            onChange={handlePresetChange}
          >
            {Object.keys(COLOR_SCALE_PRESETS).map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
            <MenuItem value="Custom" disabled>
              Custom
            </MenuItem>
          </Select>
        </FormControl>

        <div
          style={{
            height: 24,
            borderRadius: 4,
            background: stopsToCssGradient(stops),
          }}
        />
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption">{minSpeed} m/s</Typography>
          <Typography variant="caption">{((minSpeed + maxSpeed) / 2).toFixed(1)} m/s</Typography>
          <Typography variant="caption">{maxSpeed} m/s</Typography>
        </Stack>

        {displayStops.map((stop) => (
          <Stack key={stop.index} direction="row" spacing={1} alignItems="center">
            <input
              type="color"
              value={stop.color}
              onChange={(e) => handleColorChange(stop.index, e.target.value)}
              style={{
                width: 36,
                height: 28,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
              }}
            />
            <Slider
              size="small"
              value={stop.pos}
              min={0}
              max={1}
              step={0.01}
              onChange={(e, v) => handlePosChange(stop.index, v)}
              sx={{ flex: 1 }}
            />
            <Typography variant="caption" sx={{ width: 56, textAlign: 'right' }}>
              {toSpeed(stop.pos)} m/s
            </Typography>
            <IconButton
              size="small"
              disabled={stops.length <= 2}
              onClick={() => handleDelete(stop.index)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}

        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddStop}>
            Add stop
          </Button>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {copied ? 'Copied!' : 'Copy config'}
          </Button>
          <Button size="small" startIcon={<RestartAltIcon />} onClick={handleReset}>
            Reset
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

export default ColorScaleEditor;
